import {
  createColumnHelper,
  createSortedRowModel,
  type RowData,
  rowSortingFeature,
  type SortingState,
  sortFn_alphanumeric,
  sortFn_basic,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'
import { useIsSinglePane } from '../lib/use-media-query'
import { Checkbox } from './switch'

export interface DataGridColumn<T> {
  id: string
  header: string
  cell: (row: T) => React.ReactNode
  /** Value to sort by; omit to make the column unsortable. */
  sortValue?: (row: T) => string | number
  align?: 'left' | 'right'
  width?: string
  /** Hidden from tech/cover at the serializer; the grid just never renders an absent field. */
  className?: string
  /** Wrap this cell and fix the table width so a long sentence stays inside the pane (#1069). */
  wrap?: boolean
}

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, basic: sortFn_basic },
})

/**
 * Dense grid (Ready to bill, Ledger, Admin). Wraps TanStack Table v9 with our markup: sticky head,
 * sortable headers, optional selection and an expandable row panel.
 * Below 900px each row is a card, so a column cannot sit off the edge of the phone (#666).
 */
const INTERACTIVE = 'a, button, input, select, textarea, label, [role="checkbox"], [role="combobox"], [data-no-row-toggle]'

/** A click that lands on a control inside the row belongs to that control, not to the row's expand toggle. */
function interactiveTarget(target: EventTarget | null, row: Element): boolean {
  if (!(target instanceof Element)) return false
  const hit = target.closest(INTERACTIVE)
  return hit !== null && row.contains(hit)
}

export function DataGrid<T extends RowData>({
  data,
  columns,
  rowKey,
  label,
  selectable = false,
  selected,
  onSelectedChange,
  renderExpanded,
  expandedKey,
  onExpandedKeyChange,
  rowTone,
  empty,
  expandOnRowClick = false,
  expandToggle = 'end',
}: {
  data: T[]
  columns: DataGridColumn<T>[]
  rowKey: (row: T) => string
  label: string
  selectable?: boolean
  selected?: Set<string>
  onSelectedChange?: (next: Set<string>) => void
  renderExpanded?: (row: T) => React.ReactNode
  expandedKey?: string | null
  onExpandedKeyChange?: (key: string | null) => void
  rowTone?: (row: T) => 'warning' | undefined
  empty?: React.ReactNode
  /** Clicking anywhere on the row (outside a link, button or input) toggles its panel (#440). */
  expandOnRowClick?: boolean
  /** Where the expand chevron sits; `start` keeps it visible on a wide grid (#440). */
  expandToggle?: 'start' | 'end'
}) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const narrow = useIsSinglePane()
  const helper = React.useMemo(() => createColumnHelper<typeof features, T>(), [])
  const defs = React.useMemo(
    () =>
      columns.map((c) =>
        helper.accessor((row: T): unknown => (c.sortValue ? c.sortValue(row) : ''), {
          id: c.id,
          header: c.header,
          enableSorting: Boolean(c.sortValue),
          sortFn: 'alphanumeric',
        }),
      ),
    [columns, helper],
  )
  const table = useTable(
    {
      features,
      columns: defs,
      data,
      getRowId: (row: T) => rowKey(row),
      state: { sorting },
      onSortingChange: setSorting,
    },
    (state) => state,
  )
  const byId = React.useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns])
  const rows = table.getRowModel().rows
  const allKeys = data.map(rowKey)
  const allSelected = selectable && selected !== undefined && allKeys.length > 0 && allKeys.every((k) => selected.has(k))

  const toggle = (key: string) => {
    if (!selected || !onSelectedChange) return
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onSelectedChange(next)
  }
  const colSpan = columns.length + (selectable ? 1 : 0) + (renderExpanded ? 1 : 0)
  const fixed = columns.some((column) => column.wrap)

  if (narrow) {
    const labeled = columns.filter((column) => column.header)
    const actions = columns.filter((column) => !column.header)
    return (
      <div data-slot="data-grid" data-layout="cards" className="max-w-full min-w-0 space-y-3">
        {selectable ? (
          <div className="flex min-h-11 items-center">
            <Checkbox
              aria-label="Select all"
              checked={allSelected}
              onCheckedChange={(value) => onSelectedChange?.(value ? new Set(allKeys) : new Set())}
            />
          </div>
        ) : null}
        {rows.length === 0 ? empty : null}
        <ul aria-label={label} className="space-y-3">
          {rows.map((row) => {
            const original = row.original as T
            const key = row.id
            const isExpanded = expandedKey === key
            const tone = rowTone?.(original)
            return (
              <li
                key={key}
                data-row={key}
                onClick={
                  renderExpanded && expandOnRowClick
                    ? (event) => {
                        if (interactiveTarget(event.target, event.currentTarget)) return
                        onExpandedKeyChange?.(isExpanded ? null : key)
                      }
                    : undefined
                }
                onKeyDown={
                  renderExpanded && expandOnRowClick
                    ? (event) => {
                        if (event.target !== event.currentTarget) return
                        if (event.key !== 'Enter' && event.key !== ' ') return
                        event.preventDefault()
                        onExpandedKeyChange?.(isExpanded ? null : key)
                      }
                    : undefined
                }
                className={cn(
                  'rounded-lg border border-border bg-card p-3',
                  renderExpanded && expandOnRowClick && 'cursor-pointer',
                  (selected?.has(key) || isExpanded) && 'bg-accent/60',
                  tone === 'warning' && 'bg-warning-soft/40',
                )}
              >
                <div className="flex items-start gap-3">
                  {selectable ? (
                    <Checkbox
                      aria-label={`Select ${key}`}
                      checked={selected?.has(key) ?? false}
                      onCheckedChange={() => toggle(key)}
                      className="mt-1"
                    />
                  ) : null}
                  <dl className="min-w-0 flex-1 space-y-2">
                    {labeled.map((column) => (
                      <div key={column.id} className="min-w-0">
                        <dt className="text-xs font-medium text-muted-foreground">{column.header}</dt>
                        <dd className="min-w-0 break-words text-sm">{column.cell(original)}</dd>
                      </div>
                    ))}
                  </dl>
                  {renderExpanded ? (
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? `Collapse ${key}` : `Expand ${key}`}
                      onClick={() => onExpandedKeyChange?.(isExpanded ? null : key)}
                      className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded text-subtle hover:bg-muted hover:text-foreground"
                    >
                      {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                    </button>
                  ) : null}
                </div>
                {actions.map((column) => (
                  <div key={column.id} className="mt-3 min-w-0 [&_.flex]:flex-wrap">
                    {column.cell(original)}
                  </div>
                ))}
                {isExpanded && renderExpanded ? <div className="mt-3 min-w-0">{renderExpanded(original)}</div> : null}
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  return (
    <div
      data-slot="data-grid"
      className="@container max-w-full min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border bg-card"
    >
      <table aria-label={label} className={cn('border-collapse text-sm', fixed ? 'w-full table-fixed' : 'w-max min-w-full')}>
        <thead className="sticky top-0 z-10 bg-muted text-left">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {renderExpanded && expandToggle === 'start' ? <th className="w-10" aria-label="Expand" /> : null}
              {selectable ? (
                <th className="w-10 px-3 py-2">
                  <Checkbox
                    aria-label="Select all"
                    checked={allSelected}
                    onCheckedChange={(v) => onSelectedChange?.(v ? new Set(allKeys) : new Set())}
                  />
                </th>
              ) : null}
              {group.headers.map((header) => {
                const col = byId.get(header.column.id)
                const sortable = header.column.getCanSort()
                const dir = header.column.getIsSorted()
                return (
                  <th
                    key={header.id}
                    scope="col"
                    style={col?.width ? { width: col.width } : undefined}
                    aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
                    className={cn(
                      'px-3 py-2 text-xs font-semibold whitespace-nowrap text-muted-foreground',
                      col?.align === 'right' && 'text-right',
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex min-h-8 cursor-pointer items-center gap-1 hover:text-foreground max-pane:min-h-11 max-pane:px-1"
                      >
                        {col?.header}
                        {dir === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : dir === 'desc' ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ArrowUpDown className="size-3 opacity-50" />
                        )}
                      </button>
                    ) : (
                      col?.header
                    )}
                  </th>
                )
              })}
              {renderExpanded && expandToggle === 'end' ? <th className="w-10" aria-label="Expand" /> : null}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan}>{empty}</td>
            </tr>
          ) : null}
          {rows.map((row) => {
            const original = row.original as T
            const key = row.id
            const isExpanded = expandedKey === key
            const tone = rowTone?.(original)
            const toggleCell = renderExpanded ? (
              <td className="px-2">
                <button
                  type="button"
                  aria-label={isExpanded ? `Collapse ${key}` : `Expand ${key}`}
                  onClick={() => onExpandedKeyChange?.(isExpanded ? null : key)}
                  className="inline-flex size-7 cursor-pointer items-center justify-center rounded text-subtle hover:bg-muted hover:text-foreground max-pane:size-11"
                >
                  {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                </button>
              </td>
            ) : null
            return (
              <React.Fragment key={key}>
                <tr
                  data-row={key}
                  aria-expanded={renderExpanded ? isExpanded : undefined}
                  onClick={
                    renderExpanded && expandOnRowClick
                      ? (event) => {
                          if (interactiveTarget(event.target, event.currentTarget)) return
                          onExpandedKeyChange?.(isExpanded ? null : key)
                        }
                      : undefined
                  }
                  className={cn(
                    'border-t border-border transition-colors hover:bg-muted/50',
                    renderExpanded && expandOnRowClick && 'cursor-pointer',
                    (selected?.has(key) || isExpanded) && 'bg-accent/60',
                    tone === 'warning' && 'bg-warning-soft/40',
                  )}
                >
                  {expandToggle === 'start' ? toggleCell : null}
                  {selectable ? (
                    <td className="px-3 py-2.5">
                      <Checkbox aria-label={`Select ${key}`} checked={selected?.has(key) ?? false} onCheckedChange={() => toggle(key)} />
                    </td>
                  ) : null}
                  {columns.map((c) => (
                    <td
                      key={c.id}
                      className={cn(
                        'px-3 py-2.5',
                        c.wrap ? 'min-w-0 align-top break-words whitespace-normal' : 'whitespace-nowrap',
                        c.align === 'right' && 'tabular text-right',
                        c.className,
                      )}
                    >
                      {c.cell(original)}
                    </td>
                  ))}
                  {expandToggle === 'end' ? toggleCell : null}
                </tr>
                {isExpanded && renderExpanded ? (
                  <tr className="bg-canvas">
                    <td colSpan={colSpan} className="p-0">
                      {/* Stick to the visible grid, not the table's scroll width, so the packet fits the pane (#566). */}
                      <div className="sticky left-0 w-[100cqi] max-w-[100cqi] min-w-0 p-3">{renderExpanded(original)}</div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
